import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { selectRoomAlertDeviceList } from '../../../../redux/slices/deviceList/deviceListSlice';
import ListSortType from '../../../../utility/types/listSortType';
import SortableTableHeader from '../../../common/SortableTableHeader/SortableTableHeader';

const columnLabelMap = [
  { label: 'DeviceName', displayLabel: 'Device Name', sortType: ListSortType.DEVICE_NAME },
  { label: 'SensorName', displayLabel: 'Sensor Name', sortType: ListSortType.SENSOR_NAME },
  { label: 'ComponentName', displayLabel: 'Component Name', sortType: ListSortType.COMPONENT_NAME },
  { label: 'SensorType', displayLabel: 'Sensor Type', sortType: ListSortType.SENSOR_TYPE },
  { label: 'SensorChannel', displayLabel: 'Sensor Channel', sortType: ListSortType.NULLENUM },
  { label: 'Port', displayLabel: 'Port', sortType: ListSortType.NULLENUM },
  { label: 'LastReading', displayLabel: 'Last Reading', sortType: ListSortType.NULLENUM },
  { label: 'LastReceived', displayLabel: 'Last Received', sortType: ListSortType.LAST_RECEIVED },
  { label: 'DeviceType', displayLabel: 'Device Type', sortType: ListSortType.DEVICE_TYPE },
  { label: 'MACAddress', displayLabel: 'MAC Address', sortType: ListSortType.MAC_ADDRESS },
  { label: 'SerialNumber', displayLabel: 'Serial Number', sortType: ListSortType.SERIAL_NUMBER },
  { label: 'Alerts', displayLabel: 'Alerts', sortType: ListSortType.ALERT_COUNT },
];

const noop = () => {};

/* istanbul ignore next */
function ChannelListTableSkeleton({ rows, channelListColumnVisibility }) {
  const deviceList = useSelector(selectRoomAlertDeviceList);
  const hasMaxDevice = deviceList.find((d) => d.type === 'RAMAX');

  let columns = columnLabelMap;
  if (!hasMaxDevice) {
    columns = columns.filter((col) => col.label !== 'ComponentName');
  }

  const visibleColumns = columns.filter(
    (col) => channelListColumnVisibility[col.label] === true,
  );

  const skeletonStyle = {
    display: 'inline-block',
    height: '18px',
    verticalAlign: 'middle',
    background: 'linear-gradient(90deg, #e9ecef 25%, #f5f5f5 37%, #e9ecef 63%)',
    backgroundSize: '400% 100%',
    borderRadius: '4px',
    animation: 'skeleton-shimmer 1.4s ease infinite',
  };

  const keyframes = `
    @keyframes skeleton-shimmer {
      0% { background-position: 100% 50%; }
      100% { background-position: 0 50%; }
    }
  `;

  return (
    <div aria-busy="true" aria-label="Loading channels">
      <style>{keyframes}</style>
      <table className="table table-sm table-condensed table-striped my-1">
        <thead className="thead-light">
          <tr>
            <th>{' '}</th>
            {visibleColumns.map((col) => {
              if (col.sortType === ListSortType.NULLENUM) {
                return (
                  <th key={col.label} className="align-content-center">
                    <div>
                      {col.displayLabel}
                    </div>
                  </th>
                );
              }

              return (
                <React.Fragment key={col.label}>
                  <SortableTableHeader
                    onDescClick={noop}
                    label={col.displayLabel}
                    onAscClick={noop}
                    isSelected={false}
                  />
                </React.Fragment>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, rowIdx) => (
          // eslint-disable-next-line react/no-array-index-key
            <tr key={`sk-row-${rowIdx}`}>
              <td style={{ paddingLeft: '0.75rem', paddingRight: '1.0rem' }}>
                <span
                  style={{
                    ...skeletonStyle,
                    width: '18px',
                  }}
                />
              </td>
              {visibleColumns.map((col, colIdx) => (
                <td key={`sk-cell-${rowIdx}-${col.label}`}>
                  <span
                    style={{
                      ...skeletonStyle,
                      width: `${60 + ((rowIdx * 7 + colIdx * 11) % 35)}%`,
                    }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

ChannelListTableSkeleton.propTypes = {
  rows: PropTypes.number,
  channelListColumnVisibility: PropTypes.shape({
    SensorName: PropTypes.bool,
    SensorType: PropTypes.bool,
    SensorChannel: PropTypes.bool,
    Port: PropTypes.bool,
    DeviceName: PropTypes.bool,
    LastReading: PropTypes.bool,
    LastReceived: PropTypes.bool,
    DeviceType: PropTypes.bool,
    MACAddress: PropTypes.bool,
    SerialNumber: PropTypes.bool,
    Alerts: PropTypes.bool,
    ComponentName: PropTypes.bool,
  }).isRequired,
};

ChannelListTableSkeleton.defaultProps = {
  rows: 10,
};

export default ChannelListTableSkeleton;
